import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { CheckCircle2, Copy, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Confirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/orders/${orderId}`),
      axios.get(`${API}/settings`),
    ])
      .then(([o, s]) => {
        setOrder(o.data);
        setSettings(s.data);
      })
      .catch(() => toast.error('Erro ao carregar pedido'))
      .finally(() => setLoading(false));
  }, [orderId]);

  const copyPixKey = async () => {
    try {
      await navigator.clipboard.writeText(settings?.pix_key || '');
      toast.success('Chave PIX copiada!');
    } catch {
      toast.error('Falha ao copiar. Copie manualmente.');
    }
  };

  const sendToWhatsApp = () => {
    if (!order || !settings) return;
    const itemsList = order.items
      .map((i) => `${i.quantity}x ${i.product_name} - R$ ${(i.price * i.quantity).toFixed(2)}`)
      .join('\n');
    const message = `*NOVO PEDIDO #${order.id.slice(0, 8)}*\n\n` +
      `*Cliente:* ${order.customer_name}\n` +
      `*Telefone:* ${order.customer_phone}\n` +
      `*Endereço:* ${order.delivery_address}\n` +
      (order.delivery_zone ? `*Zona:* ${order.delivery_zone}\n` : '') +
      `\n*Itens:*\n${itemsList}\n\n` +
      `*Subtotal:* R$ ${order.subtotal.toFixed(2)}\n` +
      (order.delivery_fee > 0 ? `*Taxa de entrega:* R$ ${order.delivery_fee.toFixed(2)}\n` : '') +
      `*Total:* R$ ${order.total.toFixed(2)}\n` +
      `*Pagamento:* ${order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'dinheiro' ? 'Dinheiro' : 'Cartão na entrega'}\n` +
      (order.notes ? `\n*Observações:* ${order.notes}` : '');

    const whatsapp = settings.whatsapp?.replace(/\D/g, '') || '';
    const url = `https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <p className="text-[#5C4247]">Carregando...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pedido não encontrado
          </h2>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full px-8"
          >
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  const whatsappDisplay = settings?.whatsapp
    ? `(${settings.whatsapp.slice(2, 4)}) ${settings.whatsapp.slice(4, 9)}-${settings.whatsapp.slice(9)}`
    : '';

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <CheckCircle2 className="h-20 w-20 text-[#166534] mx-auto mb-4" data-testid="success-icon" />
          <h1
            className="text-4xl sm:text-5xl font-bold text-[#291014] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
            data-testid="confirmation-title"
          >
            Pedido Confirmado!
          </h1>
          <p className="text-lg text-[#5C4247]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="confirmation-subtitle">
            Obrigado, {order.customer_name}! Enviamos seu pedido para a Petiscaria Camamu.
          </p>
        </div>

        {/* WhatsApp Button - Prominent */}
        <Button
          onClick={sendToWhatsApp}
          className="w-full bg-[#25D366] hover:bg-[#1ebd5a] text-white font-bold py-6 rounded-full text-lg shadow-lg mb-6 flex items-center justify-center gap-2"
          data-testid="send-whatsapp-button"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
          Enviar pedido para o WhatsApp
        </Button>
        <p className="text-center text-sm text-[#5C4247] -mt-4 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Clique acima para confirmar seu pedido diretamente com a Petiscaria
        </p>

        {/* Order Details */}
        <div className="bg-white rounded-2xl p-6 border border-[#F3E8E3] mb-6">
          <h2 className="text-2xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="order-details-title">
            Detalhes do Pedido
          </h2>
          <div className="space-y-2 mb-4 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <p><span className="font-semibold">Pedido:</span> #{order.id.slice(0, 8)}</p>
            <p><span className="font-semibold">Endereço:</span> {order.delivery_address}</p>
            {order.delivery_zone && (
              <p><span className="font-semibold">Zona:</span> {order.delivery_zone}</p>
            )}
            <p>
              <span className="font-semibold">Pagamento:</span>{' '}
              {order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'dinheiro' ? 'Dinheiro' : 'Cartão na entrega'}
            </p>
          </div>

          <div className="border-t border-[#F3E8E3] pt-4">
            <h3 className="font-bold text-[#291014] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Itens:</h3>
            <div className="space-y-2" data-testid="order-items-list">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm" data-testid={`order-item-${idx}`}>
                  <span style={{ fontFamily: 'Manrope, sans-serif' }}>{item.quantity}x {item.product_name}</span>
                  <span className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    R$ {(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[#F3E8E3] pt-4 mt-4 space-y-1 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="flex justify-between"><span>Subtotal:</span><span>R$ {order.subtotal.toFixed(2)}</span></div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between"><span>Entrega:</span><span>R$ {order.delivery_fee.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-[#F3E8E3] mt-2">
              <span className="text-xl font-bold text-[#291014]" style={{ fontFamily: 'Outfit, sans-serif' }}>Total:</span>
              <span className="text-2xl font-bold text-[#7F1D1D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="order-total">
                R$ {order.total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {order.payment_method === 'pix' && settings?.pix_key && (
          <div className="bg-gradient-to-br from-[#EA580C]/10 to-[#7F1D1D]/10 rounded-2xl p-6 border border-[#EA580C]/30 mb-6">
            <h2 className="text-2xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="pix-section-title">
              Pagamento via PIX
            </h2>
            <p className="text-[#5C4247] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Faça o pagamento usando a chave PIX abaixo:
            </p>
            <div className="bg-white rounded-xl p-4 flex items-center justify-between mb-4">
              <span className="font-mono text-[#291014] font-semibold" data-testid="pix-key">{settings.pix_key}</span>
              <Button onClick={copyPixKey} size="sm" className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full" data-testid="copy-pix-button">
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
            <p className="text-sm text-[#5C4247]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Após o pagamento, envie o comprovante pelo WhatsApp para confirmar.
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl p-6 border border-[#F3E8E3] mb-6">
          <h2 className="text-2xl font-bold text-[#291014] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Entre em Contato
          </h2>
          <div className="space-y-3">
            {settings?.whatsapp && (
              <a
                href={`https://wa.me/${settings.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:text-[#7F1D1D] transition-colors"
                data-testid="contact-whatsapp-link"
              >
                <Phone className="h-5 w-5 text-[#EA580C]" />
                <span style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="contact-phone">{whatsappDisplay}</span>
              </a>
            )}
            {settings?.address && (
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-[#EA580C]" />
                <span style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="contact-address">{settings.address}</span>
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full border-[#7F1D1D] text-[#7F1D1D] hover:bg-[#7F1D1D] hover:text-white font-semibold py-6 rounded-full text-lg"
          data-testid="back-to-home-button"
        >
          Voltar ao Início
        </Button>
      </div>
    </div>
  );
};

export default Confirmation;
